/**
 * FilterSection Component
 * Handles facility, provider, and advanced filter selection
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Calendar } from 'lucide-react';
import MultiSelect from '../common/MultiSelect';
import LoaderComponent from '@/components/common/LoaderComponent';

interface FilterOption {
  value: string;
  label: string;
}

interface FacilityOption {
  value: number;
  label: string;
}

interface ProviderOption {
  value: number;
  label: string;
}

interface FilterSectionProps {
  // State
  selectedFacilities: number[];
  selectedProviders: number[];
  selectedPrimaryDx: string[];
  selectedSecondaryDx: string[];
  selectedProgramEligibility: string[];
  selectedPrimaryInsurance: string[];
  selectedSecondaryInsurance: string[];
  selectedInsuranceType: string[];
  zipCodes: string;
  lastVisitDate: string;
  excludeAssignedGroups: boolean;
  showFilters: boolean;
  filteredPatientsCount: number;
  isLoading: boolean;
  selectedClinic: string;
  isDuplicateMode?: boolean;
  
  // Options
  facilityOptions: FacilityOption[];
  providerOptions: ProviderOption[];
  primaryDxOptions: FilterOption[];
  secondaryDxOptions: FilterOption[];
  primaryInsuranceOptions: FilterOption[];
  secondaryInsuranceOptions: FilterOption[];
  insuranceTypeOptions: FilterOption[];
  
  // Actions
  onFacilitiesChange: (values: number[]) => void;
  onProvidersChange: (values: number[]) => void;
  onPrimaryDxChange: (values: string[]) => void;
  onSecondaryDxChange: (values: string[]) => void;
  onProgramEligibilityChange: (values: string[]) => void;
  onPrimaryInsuranceChange: (values: string[]) => void;
  onSecondaryInsuranceChange: (values: string[]) => void;
  onInsuranceTypeChange: (values: string[]) => void;
  onZipCodesChange: (value: string) => void;
  onLastVisitDateChange: (value: string) => void;
  onExcludeAssignedGroupsChange: (value: boolean) => void;
  onApplyFilters: () => void;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  selectedFacilities,
  selectedProviders,
  selectedPrimaryDx,
  selectedSecondaryDx,
  selectedProgramEligibility,
  selectedPrimaryInsurance,
  selectedSecondaryInsurance,
  selectedInsuranceType,
  zipCodes,
  lastVisitDate,
  excludeAssignedGroups,
  showFilters,
  filteredPatientsCount,
  isLoading,
  selectedClinic,
  isDuplicateMode = false,
  facilityOptions,
  providerOptions,
  primaryDxOptions,
  secondaryDxOptions,
  primaryInsuranceOptions,
  secondaryInsuranceOptions,
  insuranceTypeOptions,
  onFacilitiesChange,
  onProvidersChange,
  onPrimaryDxChange,
  onSecondaryDxChange,
  onProgramEligibilityChange,
  onPrimaryInsuranceChange,
  onSecondaryInsuranceChange,
  onInsuranceTypeChange,
  onZipCodesChange,
  onLastVisitDateChange,
  onExcludeAssignedGroupsChange,
  onApplyFilters
}) => {
  return (
    <div>
      <h3 className="text-lg font-medium text-foreground mb-4">Select Clinics and Providers</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Select Facility Name */}
        <div className="min-w-0">
          <label className="block text-sm font-medium text-foreground mb-2">
            Select Facility Name
          </label>
          <MultiSelect
            options={facilityOptions}
            value={selectedFacilities}
            onChange={(values) => onFacilitiesChange(values as number[])}
            placeholder="Select Facility Name"
            className="w-full"
          />
        </div>

        {/* Select Provider */}
        <div className="min-w-0">
          <label className="block text-sm font-medium text-foreground mb-2">
            Select Provider
          </label>
          <MultiSelect
            options={providerOptions}
            value={selectedProviders}
            onChange={(values) => onProvidersChange(values as number[])}
            placeholder="Select Provider"
            className="w-full"
            disabled={selectedFacilities.length === 0}
          />
        </div>

        {/* Apply Button */}
        <div className="flex items-end">
          <Button
            onClick={onApplyFilters}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2 w-full"
            disabled={!selectedClinic || selectedFacilities.length === 0 || selectedProviders.length === 0}
          >
            Apply
          </Button>
        </div>
      </div>

      {/* Filters Section - Only show after Apply is clicked */}
      {showFilters && (
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">Filters</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Primary Dx */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Primary Dx
              </label>
              <MultiSelect
                options={primaryDxOptions}
                value={selectedPrimaryDx}
                onChange={(values) => onPrimaryDxChange(values as string[])}
                placeholder="Select Primary Dx"
                className="w-full"
              />
            </div>

            {/* Secondary Dx */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Secondary Dx
              </label>
              <MultiSelect
                options={secondaryDxOptions}
                value={selectedSecondaryDx}
                onChange={(values) => onSecondaryDxChange(values as string[])}
                placeholder="Select Secondary Dx"
                className="w-full"
              />
            </div>

            {/* Program Eligibility */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Program Eligibility
              </label>
              <MultiSelect
                options={[
                  { value: 'PCM', label: 'PCM' },
                  { value: 'CCM', label: 'CCM' },
                  { value: 'RPM', label: 'RPM' }
                ]}
                value={selectedProgramEligibility}
                onChange={(values) => onProgramEligibilityChange(values as string[])}
                placeholder="Select Program Eligibility"
                className="w-full"
                searchable={true}
              />
            </div>

            {/* Primary Insurance */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Primary Insurance
              </label>
              <MultiSelect
                options={primaryInsuranceOptions}
                value={selectedPrimaryInsurance}
                onChange={(values) => onPrimaryInsuranceChange(values as string[])}
                placeholder="Select Primary Insurance"
                className="w-full"
              />
            </div>

            {/* Secondary Insurance */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Secondary Insurance
              </label>
              <MultiSelect
                options={secondaryInsuranceOptions}
                value={selectedSecondaryInsurance}
                onChange={(values) => onSecondaryInsuranceChange(values as string[])}
                placeholder="Select Secondary Insurance"
                className="w-full"
              />
            </div>

            {/* Insurance Type */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Insurance Type
              </label>
              <MultiSelect
                options={insuranceTypeOptions}
                value={selectedInsuranceType}
                onChange={(values) => onInsuranceTypeChange(values as string[])}
                placeholder="Select Insurance Type"
                className="w-full"
              />
            </div>
          </div>

          {/* Additional Filter Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Zip Codes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Enter Zip Codes Separated by Commas
              </label>
              <Input
                placeholder="Enter zip codes..."
                value={zipCodes}
                onChange={(e) => onZipCodesChange(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Last Visit Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Last Visit Date
              </label>
              <div className="relative">
                <Input
                  type="date"
                  value={lastVisitDate}
                  onChange={(e) => onLastVisitDateChange(e.target.value)}
                  className="w-full"
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Exclude Patients Toggle */}
            <div className="flex items-center space-x-3 mt-8">
              <Switch
                checked={excludeAssignedGroups}
                onCheckedChange={onExcludeAssignedGroupsChange}
              />
              <span className="text-sm text-foreground">Exclude Patients with Assigned Groups</span>
            </div>
          </div>

          {/* Filter Patients Button */}
          <div className="flex items-center space-x-4 mb-6">
            <Button
              onClick={onApplyFilters}
              disabled={isLoading}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-2"
            >
              {isLoading ? 'Loading...' : 'Filter Patients'}
            </Button>
            
            {filteredPatientsCount > 0 && (
              <span className="text-lg font-medium text-foreground">
                Number of Patients Found: {filteredPatientsCount}
              </span>
            )}
          </div>
          
          {/* Show loader with text for duplicate mode */}
          {isLoading && isDuplicateMode && (
            <div className="mb-6">
              <LoaderComponent size="lg" className="h-32" />
              <div className="text-center mt-4">
                <p className="text-muted-foreground text-lg font-medium">Copying and loading data...</p>
                <p className="text-muted-foreground text-sm mt-2">Please wait while we copy all filters and settings from the original group.</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};